/**
 * Calendar utility functions for generating iCalendar (.ics) files
 * Implements RFC 5545 standard for maximum compatibility
 */

import { getCountryTimezone } from './timezoneUtils';

/**
 * Format a date in iCalendar format (YYYYMMDDTHHmmssZ for UTC or with TZID)
 * @param {Date} date - Date object to format
 * @param {boolean} useUTC - Whether to use UTC time (default: true)
 * @param {string} timezone - Optional timezone identifier (e.g., 'Africa/Casablanca')
 * @returns {string} Formatted date string
 */
export const formatICalDate = (date, useUTC = true, timezone = null) => {
  if (!date || !(date instanceof Date)) {
    throw new Error('Invalid date provided');
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  if (useUTC && !timezone) {
    // Convert to UTC
    const utcDate = new Date(date.toISOString());
    const utcYear = utcDate.getUTCFullYear();
    const utcMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
    const utcDay = String(utcDate.getUTCDate()).padStart(2, '0');
    const utcHours = String(utcDate.getUTCHours()).padStart(2, '0');
    const utcMinutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
    const utcSeconds = String(utcDate.getUTCSeconds()).padStart(2, '0');
    return `${utcYear}${utcMonth}${utcDay}T${utcHours}${utcMinutes}${utcSeconds}Z`;
  }

  // Format in local time (for TZID usage)
  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
};

/**
 * Format a date in a specific timezone for iCalendar format
 * @param {Date} date - Date object to format
 * @param {string} timezone - Timezone identifier (e.g., 'Africa/Casablanca')
 * @returns {string} Formatted date string in the specified timezone
 */
export const formatICalDateInTimezone = (date, timezone) => {
  if (!date || !(date instanceof Date)) {
    throw new Error('Invalid date provided');
  }
  if (!timezone) {
    throw new Error('Timezone is required');
  }

  // Format date in the specified timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;
  const hour = parts.find(p => p.type === 'hour').value;
  const minute = parts.find(p => p.type === 'minute').value;
  const second = parts.find(p => p.type === 'second').value;

  return `${year}${month}${day}T${hour}${minute}${second}`;
};

/**
 * Format a date as date-only in iCalendar format (YYYYMMDD)
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string
 */
export const formatICalDateOnly = (date) => {
  if (!date || !(date instanceof Date)) {
    throw new Error('Invalid date provided');
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

/**
 * Escape text for iCalendar format (RFC 5545)
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
export const escapeICalText = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
};

/**
 * Generate a unique ID for the calendar event
 * @returns {string} Unique identifier
 */
export const generateEventUID = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}@visa-checker`;
};

/**
 * Generate iCalendar (.ics) file content
 * @param {Object} visaData - Visa information
 * @param {Date} visaData.expiryDate - Visa expiration date
 * @param {string} visaData.selectedDate - Visa issue date (YYYY-MM-DD format)
 * @param {Object} visaData.selectedCountry - Country object with name, countryCode, etc.
 * @param {number} visaData.visaDuration - Visa duration in days
 * @param {string} visaData.status - Visa status ('Valid' or 'Invalid')
 * @returns {string} iCalendar file content
 */
export const generateICalendarContent = (visaData) => {
  const {
    expiryDate,
    selectedDate,
    selectedCountry,
    visaDuration,
    status
  } = visaData;

  // Validate required data
  if (!expiryDate || !selectedDate || !selectedCountry) {
    throw new Error('Missing required visa data for calendar generation');
  }

  // Get country name
  const countryName = typeof selectedCountry === 'string' 
    ? selectedCountry 
    : selectedCountry.name || 'Unknown Country';

  // Get country timezone information
  const countryTz = getCountryTimezone(selectedCountry);
  const timezone = countryTz?.timezone || null;
  const timezoneName = countryTz?.name || null;
  const timezoneAbbr = countryTz?.abbreviation || null;

  // Parse issue date
  let issueDate;
  if (selectedDate instanceof Date) {
    issueDate = new Date(selectedDate);
  } else {
    const [year, month, day] = selectedDate.split('-').map(Number);
    issueDate = new Date(year, month - 1, day);
  }

  // Ensure expiryDate is a Date object
  const expiry = expiryDate instanceof Date ? expiryDate : new Date(expiryDate);

  // Calculate start date: 5 days before expiry at 2:00 PM (14:00) in country's timezone
  // Calendar apps will automatically display in user's preferred format (24h or 12h)
  let reminderStartDate;
  let reminderEndDate;
  let dtstart, dtend;
  
  if (timezone) {
    // Get expiry date components in the country's timezone
    const expiryFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const expiryParts = expiryFormatter.formatToParts(expiry);
    const expiryYear = parseInt(expiryParts.find(p => p.type === 'year').value);
    const expiryMonth = parseInt(expiryParts.find(p => p.type === 'month').value);
    const expiryDay = parseInt(expiryParts.find(p => p.type === 'day').value);
    
    // Calculate 5 days before expiry
    const startDate = new Date(expiryYear, expiryMonth - 1, expiryDay - 5);
    const startYear = startDate.getFullYear();
    const startMonth = String(startDate.getMonth() + 1).padStart(2, '0');
    const startDay = String(startDate.getDate()).padStart(2, '0');
    
    // Format directly as iCalendar format in country timezone: YYYYMMDDTHHmmss
    // Using 14:00 (2:00 PM) and 15:00 (3:00 PM) for midday reminder
    dtstart = `${startYear}${startMonth}${startDay}T140000`;
    dtend = `${startYear}${startMonth}${startDay}T150000`;
    
    // Create Date objects for description formatting (using local time as approximation)
    reminderStartDate = new Date(expiryYear, expiryMonth - 1, expiryDay - 5, 14, 0, 0);
    reminderEndDate = new Date(expiryYear, expiryMonth - 1, expiryDay - 5, 15, 0, 0);
  } else {
    // Fallback: use local timezone if country timezone not found
    reminderStartDate = new Date(expiry);
    reminderStartDate.setDate(reminderStartDate.getDate() - 5);
    reminderStartDate.setHours(14, 0, 0, 0); // 2:00 PM
    
    reminderEndDate = new Date(reminderStartDate);
    reminderEndDate.setHours(15, 0, 0, 0); // 3:00 PM
    
    dtstart = formatICalDate(reminderStartDate, true);
    dtend = formatICalDate(reminderEndDate, true);
  }

  // Generate unique ID
  const uid = generateEventUID();

  // Current timestamp for DTSTAMP (always UTC)
  const now = new Date();

  // Format dates
  const dtstamp = formatICalDate(now, true);
  
  // dtstart and dtend are already set above in the timezone calculation block

  // Format dates for description (using consistent locale-aware formatting)
  const formatDateForDescription = (date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Build description - structured and human-readable
  const descriptionParts = [
    `⚠️ Visa Expiration Reminder for your ${visaDuration}-Day Visa`,
    ``,
    `❗ Reminder appears 5 days before expiration for 3 days in a row.`,
    ``,
    `✅ Issuing Date: ${formatDateForDescription(issueDate)}`,
    `❌ Expiration Date: ${formatDateForDescription(expiry)}`
  ];
  
  // Add timezone information if available
  if (timezoneName && timezoneAbbr) {
    descriptionParts.push(``);
    descriptionParts.push(`🕐 Times are in ${countryName} timezone (${timezoneName}, ${timezoneAbbr})-`);
  }
  
  const description = escapeICalText(descriptionParts.join('\n'));

  // Build summary (title) - include visa duration and yellow exclamation mark
  const summary = escapeICalText(`⚠️ Visa Expiration - ${countryName} ${visaDuration}-Day Visa`);

  // Build location
  const location = escapeICalText(countryName);

  // Generate iCalendar content
  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Visa Checker//Visa Expiration//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH'
  ];

  // Add VTIMEZONE if timezone is specified
  if (timezone) {
    // Add VTIMEZONE definition (simplified - calendar apps will handle DST automatically)
    icsLines.push('BEGIN:VTIMEZONE');
    icsLines.push(`TZID:${timezone}`);
    icsLines.push('BEGIN:STANDARD');
    icsLines.push('DTSTART:19700101T000000');
    icsLines.push(`TZOFFSETFROM:+0000`);
    icsLines.push(`TZOFFSETTO:+0000`);
    icsLines.push(`TZNAME:${timezoneAbbr || 'UTC'}`);
    icsLines.push('END:STANDARD');
    icsLines.push('END:VTIMEZONE');
  }

  // Add VEVENT
  icsLines.push('BEGIN:VEVENT');
  icsLines.push(`UID:${uid}`);
  icsLines.push(`DTSTAMP:${dtstamp}`);
  
  // Add TZID parameter if timezone is specified
  if (timezone) {
    icsLines.push(`DTSTART;TZID=${timezone}:${dtstart}`);
    icsLines.push(`DTEND;TZID=${timezone}:${dtend}`);
  } else {
    icsLines.push(`DTSTART:${dtstart}`);
    icsLines.push(`DTEND:${dtend}`);
  }
  
  icsLines.push(`SUMMARY:${summary}`);
  icsLines.push(`DESCRIPTION:${description}`);
  icsLines.push(`LOCATION:${location}`);
  icsLines.push('STATUS:CONFIRMED');
  icsLines.push('SEQUENCE:0');
  // Recur daily for 3 days (3 occurrences total)
  icsLines.push('RRULE:FREQ=DAILY;COUNT=3');
  icsLines.push('END:VEVENT');
  icsLines.push('END:VCALENDAR');

  const icsContent = icsLines.join('\r\n');

  return icsContent;
};

/**
 * Download calendar file
 * @param {string} content - iCalendar file content
 * @param {string} filename - Filename for the download (default: visa-expiration.ics)
 */
export const downloadCalendarFile = (content, filename = 'visa-expiration.ics') => {
  try {
    // Create blob with proper MIME type
    const blob = new Blob([content], { 
      type: 'text/calendar;charset=utf-8' 
    });

    // Create object URL
    const url = URL.createObjectURL(blob);

    // Create temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke object URL after a short delay to ensure download starts
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Error downloading calendar file:', error);
    throw new Error('Failed to download calendar file');
  }
};

/**
 * Generate and download calendar file in one step
 * @param {Object} visaData - Visa information (see generateICalendarContent)
 * @param {string} customFilename - Optional custom filename
 */
export const generateAndDownloadCalendar = (visaData, customFilename = null) => {
  try {
    // Generate calendar content
    const content = generateICalendarContent(visaData);

    // Generate filename if not provided
    const countryName = typeof visaData.selectedCountry === 'string'
      ? visaData.selectedCountry
      : visaData.selectedCountry?.name || 'visa';

    const expiryDate = visaData.expiryDate instanceof Date
      ? visaData.expiryDate
      : new Date(visaData.expiryDate);

    const dateStr = expiryDate.toISOString().split('T')[0];
    const filename = customFilename || `visa-expiration-${countryName.toLowerCase().replace(/\s+/g, '-')}-${dateStr}.ics`;

    // Download file
    downloadCalendarFile(content, filename);

    return { success: true, filename };
  } catch (error) {
    console.error('Error generating calendar file:', error);
    return { success: false, error: error.message };
  }
};


